import { useId } from 'react';
import type { ChangeEventHandler, FormEventHandler } from 'react';
import Button from 'components/shared/Buttons/BaseButton';
import { CalculatorInput } from 'components/Widget/CalculatorInput/CalculatorInput';
import { HintDisclosure } from 'components/Widget/shared/HintDisclosure';
import type { CurrencyFieldProps } from 'components/Widget/shared/useCurrencyFields';
import type { PercentFieldProps } from 'components/Widget/shared/usePercentFields';
import type { CalculatorField, CalculatorFieldErrors } from 'components/Widget/shared/types';
import {
  APY_FIELD,
  CALCULATE_LABEL,
  CONTRIBUTION_FREQUENCY_FIELD,
  CONTRIBUTION_FREQUENCY_OPTIONS,
  RECURRING_CONTRIBUTION_FIELD,
  RESET_LABEL,
  SAVINGS_PERIOD_FIELD,
  SAVINGS_PERIOD_OPTIONS,
  STARTING_BALANCE_FIELD,
  TITLE,
} from './constants';
import type { ContributionFrequencyKey, SavingsPeriodKey } from './types';

const CURRENCY_AFFIX = { symbol: '$', position: 'prefix' } as const;

const LABEL = 'block text-body-saans';

const SELECT =
  'mt-3 h-16 w-full rounded-lg border border-content-secondary bg-bg-primary px-6 text-body-saans text-content-primary outline-none transition-shadow hover:ring-[6px] hover:ring-bg-tertiary focus:border-content-primary focus:ring-[6px] focus:ring-bg-tertiary';

interface SelectFieldProps<Key extends string> {
  field: CalculatorField;
  options: readonly { key: Key; label: string }[];
  value: Key;
  onChange: (key: Key) => void;
}

const SelectField = <Key extends string>({ field, options, value, onChange }: SelectFieldProps<Key>) => {
  const selectId = useId();
  const hintId = `${selectId}-hint`;
  const handleChange: ChangeEventHandler<HTMLSelectElement> = event => {
    const selected = options.find(option => option.key === event.target.value);

    if (selected) onChange(selected.key);
  };

  const labelElement = (
    <label htmlFor={selectId} className={LABEL}>
      {field.label}
    </label>
  );

  return (
    <div className="w-full">
      {field.hint ? (
        <div className="flex items-center gap-2">
          {labelElement}
          <HintDisclosure hintId={hintId} label={field.label} hint={field.hint} />
        </div>
      ) : (
        labelElement
      )}
      <select
        id={selectId}
        aria-describedby={field.hint ? hintId : undefined}
        value={value}
        onChange={handleChange}
        className={SELECT}>
        {options.map(option => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

interface ApySavingsCalculatorFormProps {
  errors: CalculatorFieldErrors;
  currencyFieldProps: CurrencyFieldProps;
  apyFieldProps: PercentFieldProps;
  contributionFrequency: ContributionFrequencyKey;
  savingsPeriod: SavingsPeriodKey;
  onContributionFrequencyChange: (key: ContributionFrequencyKey) => void;
  onSavingsPeriodChange: (key: SavingsPeriodKey) => void;
  onCalculate: () => void;
  onReset: () => void;
}

export const ApySavingsCalculatorForm = ({
  errors,
  currencyFieldProps,
  apyFieldProps,
  contributionFrequency,
  savingsPeriod,
  onContributionFrequencyChange,
  onSavingsPeriodChange,
  onCalculate,
  onReset,
}: ApySavingsCalculatorFormProps) => {
  const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    onCalculate();
  };

  return (
    <form aria-label={TITLE} className="flex flex-col gap-6" onSubmit={handleSubmit}>
      <div className="grid gap-6 sm:grid-cols-2">
        {/* Native validation would pre-empt the calculator's custom range rules. */}
        <CalculatorInput
          {...currencyFieldProps(STARTING_BALANCE_FIELD, errors[STARTING_BALANCE_FIELD.label])}
          affix={CURRENCY_AFFIX}
          collapsibleHint
        />
        <CalculatorInput
          {...currencyFieldProps(RECURRING_CONTRIBUTION_FIELD, errors[RECURRING_CONTRIBUTION_FIELD.label])}
          affix={CURRENCY_AFFIX}
          collapsibleHint
        />
      </div>

      <SelectField
        field={CONTRIBUTION_FREQUENCY_FIELD}
        options={CONTRIBUTION_FREQUENCY_OPTIONS}
        value={contributionFrequency}
        onChange={onContributionFrequencyChange}
      />

      <SelectField
        field={SAVINGS_PERIOD_FIELD}
        options={SAVINGS_PERIOD_OPTIONS}
        value={savingsPeriod}
        onChange={onSavingsPeriodChange}
      />

      <CalculatorInput {...apyFieldProps(APY_FIELD, errors[APY_FIELD.label])} collapsibleHint />

      <div className="flex flex-col gap-4 sm:flex-row">
        <Button type="submit">{CALCULATE_LABEL}</Button>
        <Button type="button" variant="outline" onClick={onReset}>
          {RESET_LABEL}
        </Button>
      </div>
    </form>
  );
};
